import { Injectable } from "@angular/core";
import { MoteService } from "./mote.service";
import { ProjectService, ProjectChangeEv } from "./project.service";
import { pluck, distinctUntilChanged, switchMap, map, filter, tap, debounceTime, throttleTime, take } from "rxjs/operators";
import { Observable, zip, combineLatest, asyncScheduler } from "rxjs";
import { UserService } from "./user.service";
import { User, Project, Mote } from "../models";
import { UserMoteService } from "./user-mote.service";
import { Language } from '@motes-sdk/models/language';

@Injectable({ providedIn: 'root' })
export class UserProjectService {

    private initialized: boolean = false;

    constructor(private userService: UserService, private moteService: MoteService, private userMoteService: UserMoteService, private projectService: ProjectService) {
    }

    public init() {
        if(this.initialized) return;
        this.initialized = true;
    }

    /**
     * Watch projects where current user is member
     */
    public watchUserProjects(): Observable<[User, Project[]]> {
        let watchUser = this.userService.watchCurrentUser();
        let switchUserProjects = switchMap((user: User) => this.projectService.watchMemberProjects(user ? user.key : ""));
        // Return observable of user and user projects fired in same event
        // return zip(watchUser, watchUser.pipe(switchUserProjects));
        // We have to fire a new event on both user updte and project update (as change project list is not contained in user)
        return combineLatest(watchUser, watchUser.pipe(switchUserProjects)).pipe(throttleTime(600, asyncScheduler, { leading: true, trailing: true }));
    }

    public getUserProjects(): Promise<[User, Project[]]> {
        return this.watchUserProjects().pipe(take(1)).toPromise();
    }

    /**
     * Delete all projects that user owns
     */
    public async deleteUserProjects(userKey: string): Promise<boolean> {
        let projects = await this.projectService.getOwnerProjects(userKey);

        let deleteAll = projects.map((project) => this.projectService.deleteProject(project.key));
        let deleteSuccess = await Promise.all(deleteAll);

        return deleteSuccess.every((success) => success);
    }

    /**
     * Watch all motes of projects belonging to the active user
     */
    public watchUserProjectMotes(showDeleted: boolean = false) {
        let watchProjectKeys = this.watchUserProjects().pipe(map(([user, projects]) => projects.map(project => project.key)));
        return watchProjectKeys.pipe(switchMap(keys => this.moteService.watchProjectMotes(keys, showDeleted)));
    }

    /**
     * Watch motes of the active route or motes from the project with the given key and augment user data
     * Use project service instead of user-project service if augmented user state is not needed / desired
     */
    public watchActiveRouteMotes() {
        let watchProjectMotes = this.watchActiveProjectMotes();
        let watchProject: Observable<ProjectChangeEv> = this.projectService.watchActiveProject();
        let watchRouteKey = watchProject.pipe(pluck('project'), pluck('activeRouteKey'), distinctUntilChanged());
        let watchProjectMotesAndRouteKey = combineLatest(watchProjectMotes, watchRouteKey);

        // Motes of active project (distinct until project key changes (not until projectEvent changes))
        return watchProjectMotesAndRouteKey.pipe(map(([motes, activeRoute]) => {
            return motes.filter(mote => {
                return (mote.key == activeRoute) || (mote.parent == activeRoute) || (!mote.parent && !mote.canHaveChildren());
            });
        }));
    }

    /**
     *
     * Watch motes of the active project
     */
    public watchActiveProjectMotes(): Observable<Mote[]> {
        return this.userMoteService.watchProjectMotes().pipe(pluck('motes'));
    }

    /**
     * Watch routes/collections of the active project
     */
    public watchActiveProjectRoutes(languageObserver: Observable<Language> = null) {
        let watchProject: Observable<ProjectChangeEv> = this.projectService.watchActiveProject();
        let watchProjectKey = watchProject.pipe(pluck('project'), pluck('key'), distinctUntilChanged());
        // Motes of active project (distinct until project key changes (not until projectEvent changes))
        return watchProjectKey.pipe(switchMap(projectKey => this.moteService.watchCollectionMotes(projectKey, languageObserver)));
    }

}
